import { useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { createContext, useContext, type ReactNode } from "react";

import { DEFAULT_LOCALE, LOCALE_LABELS, activeLocales, messagesFor, type Locale } from "./config";
import { setLocale } from "./locale.functions";

const LocaleContext = createContext<Locale>(DEFAULT_LOCALE);

export function LocaleProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const locale = useContext(LocaleContext);
  return { locale, t: messagesFor(locale) };
}

/** Rendered only when two or more languages are active. */
export function LanguageSwitcher({ className = "" }: { className?: string }) {
  const { locale, t } = useLocale();
  const router = useRouter();
  const save = useServerFn(setLocale);
  const locales = activeLocales();
  if (locales.length < 2) return null;
  return (
    <select
      aria-label={t.nav.language}
      value={locale}
      className={`fc-body bg-transparent text-foreground ${className}`}
      onChange={async (e) => {
        await save({ data: { locale: e.target.value as Locale } });
        await router.invalidate();
      }}
    >
      {locales.map((l) => (
        <option key={l} value={l}>
          {LOCALE_LABELS[l]}
        </option>
      ))}
    </select>
  );
}
